import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveOrg } from "@/hooks/useActiveOrg";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Users, BookOpen, Clock, TrendingUp, Plus, Edit } from "lucide-react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import Seo from "@/components/seo/Seo";

interface TutoringSchedule {
  id: string;
  student_email: string;
  student_name: string | null;
  session_date: string;
  start_time: string;
  end_time: string;
  subject: string;
  topic: string | null;
  status: string;
}

interface KpiCounts {
  totalTests: number;
  publishedTests: number;
  totalStudents: number;
  totalLists: number;
  recentResults: number;
}

export default function InstructorDashboard() {
  const { user } = useAuth();
  const { activeOrgId, activeOrg } = useActiveOrg();
  const [todaysSchedules, setTodaysSchedules] = useState<TutoringSchedule[]>([]);
  const [counts, setCounts] = useState<KpiCounts>({
    totalTests: 0,
    publishedTests: 0,
    totalStudents: 0,
    totalLists: 0,
    recentResults: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    (async () => {
      try {
        const today = new Date().toISOString().split("T")[0];

        // Org scoping: when an org is active, scope to org_id; otherwise show
        // the user's personal rows (org_id IS NULL) plus their own creations.
        const scope = <T extends { eq: (...a: unknown[]) => T; is: (...a: unknown[]) => T }>(
          q: T,
        ): T => {
          if (activeOrgId) return q.eq("org_id", activeOrgId);
          return q.eq("instructor_id", user.id);
        };

        const [
          totalTestsRes,
          publishedRes,
          listsRes,
          studentsRes,
          schedulesRes,
          resultsRes,
        ] = await Promise.all([
          scope(
            supabase
              .from("instructor_tests")
              .select("id", { count: "exact", head: true }) as never,
          ),
          scope(
            supabase
              .from("instructor_tests")
              .select("id", { count: "exact", head: true })
              .eq("is_published", true) as never,
          ),
          scope(
            supabase
              .from("instructor_student_lists")
              .select("id", { count: "exact", head: true }) as never,
          ),
          scope(
            supabase
              .from("instructor_student_lists")
              .select("id, instructor_student_emails(id)") as never,
          ),
          scope(
            supabase
              .from("tutoring_schedules")
              .select("*")
              .eq("session_date", today)
              .order("start_time", { ascending: true }) as never,
          ),
          activeOrgId
            ? supabase
                .from("test_invitations")
                .select("id, instructor_tests!inner(org_id)", { count: "exact", head: true })
                .eq("instructor_tests.org_id", activeOrgId)
                .not("completed_at", "is", null)
            : supabase
                .from("test_invitations")
                .select("id, instructor_tests!inner(instructor_id)", { count: "exact", head: true })
                .eq("instructor_tests.instructor_id", user.id)
                .not("completed_at", "is", null),
        ]);

        const totalStudents = ((studentsRes as { data?: Array<{ instructor_student_emails?: Array<unknown> }> }).data || []).reduce(
          (acc: number, l) => acc + (l.instructor_student_emails?.length || 0),
          0,
        );

        setCounts({
          totalTests: (totalTestsRes as { count: number | null }).count ?? 0,
          publishedTests: (publishedRes as { count: number | null }).count ?? 0,
          totalLists: (listsRes as { count: number | null }).count ?? 0,
          totalStudents,
          recentResults: (resultsRes as { count: number | null }).count ?? 0,
        });
        setTodaysSchedules(((schedulesRes as { data?: TutoringSchedule[] }).data) || []);
      } catch (e) {
        console.error("Error loading instructor dashboard", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [user, activeOrgId]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Seo title="Instructor Dashboard" description="Manage your students, sessions and tests." canonicalPath="/instructor" noindex />
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Welcome back</h1>
        <p className="text-muted-foreground">
          {activeOrg
            ? `Showing data for ${activeOrg.org_name}.`
            : "Showing your personal data. Switch to an organization in the topbar to see org-scoped data."}
        </p>
      </header>

      {/* KPI Tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tests</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.totalTests}</div>
            <p className="text-xs text-muted-foreground">{counts.publishedTests} published</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Today's Sessions</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todaysSchedules.length}</div>
            <p className="text-xs text-muted-foreground">Scheduled for today</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Students</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.totalStudents}</div>
            <p className="text-xs text-muted-foreground">Across {counts.totalLists} lists</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed Tests</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{counts.recentResults}</div>
            <p className="text-xs text-muted-foreground">All-time submissions</p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Today's Schedule</span>
            <Button asChild size="sm">
              <Link to="/instructor/tutoring/create">
                <Plus className="h-4 w-4 mr-2" />
                Add Session
              </Link>
            </Button>
          </CardTitle>
          <CardDescription>Your tutoring sessions scheduled for today</CardDescription>
        </CardHeader>
        <CardContent>
          {todaysSchedules.length > 0 ? (
            <div className="space-y-3">
              {todaysSchedules.map((schedule) => (
                <div key={schedule.id} className="flex items-center justify-between p-3 border rounded-lg bg-card">
                  <div>
                    <h4 className="font-medium">{schedule.student_name || schedule.student_email}</h4>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {format(new Date(`2024-01-01T${schedule.start_time}`), "h:mm a")} -{" "}
                        {format(new Date(`2024-01-01T${schedule.end_time}`), "h:mm a")}
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="h-3 w-3" />
                        {schedule.subject}
                      </span>
                      {schedule.topic && <span className="text-xs">• {schedule.topic}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={
                        schedule.status === "completed"
                          ? "secondary"
                          : schedule.status === "cancelled"
                          ? "destructive"
                          : "default"
                      }
                    >
                      {schedule.status}
                    </Badge>
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/instructor/tutoring/edit/${schedule.id}`}>
                        <Edit className="h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No sessions scheduled for today</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
